import React, { useEffect, useRef, useState } from 'react';
import { View, TextInput, Pressable } from 'react-native';
import { useLazyQuery } from '@apollo/client';
import styled from 'styled-components/native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';

import { Text, Hint } from 'app/src/styles';
import * as colors from 'app/src/styles/colors';

import { AUTOCOMPLETE } from './queries';
import Result from './Result';
import resultLabel from './resultLabel';

// An open list has to outrank every other field on the form, and a closed one
// must never cover its neighbours.
const OPEN_LIST_ZINDEX = 9999;

// Long enough for a click on a result to land before blur closes the list.
export const RESULT_CLICK_GRACE_MS = 150;

const INPUT_OPTION_KEYS = { make: 'makes', model: 'models' };

const Container = styled(View)`
  flex-direction: row;
  background-color: white;
  align-self: left;
  align-items: center;
  height: 32px;
  padding-left: 8px;
  padding-right: 8px;
  margin-top: 0px;
  margin-right: 40px;
  margin-bottom: 10px;
  border-style: solid;
  min-width: 100%;
  position: relative;

  /* The input's own focus ring is suppressed and the box a seller sees is this
     container, so it carries the focused state instead. */
  border-width: ${props => (props.$focused ? '2px' : '1px')};
  border-color: ${props => (props.$focused ? colors.elRed : 'black')};
`;

const Input = styled(TextInput)`
  outline-width: 0px;
  outline-color: white;
  margin-left: 12px;
  width: 90%;
`;

const Results = styled(View)`
  position: absolute;
  top: 30px;
  left: 5px;
  background-color: white;
  border-width: 1px;
  border-style: solid;
  border-color: black;
  padding-top: 5px;
  width: 200%;
  max-height: 240px;
  overflow-y: auto;
`;

const Autocomplete = ({ resource, label, placeHolder, setValue, name,
    setResource, defaultValue, zIndex, hint
}) => {
  const [term, setTerm] = useState(resultLabel(defaultValue));
  const [resources, setResources] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [focused, setFocused] = useState(false);
  const [getResults, { data }] = useLazyQuery(AUTOCOMPLETE);
  const blurTimeout = useRef();
  const input = useRef();

  const isInputOption = resource === 'InputOption';
  const minimumTermLength = name === 'model' ? 1 : 2;

  useEffect(() => () => clearTimeout(blurTimeout.current), []);

  useEffect(() => {
    if (showResults && term.length >= minimumTermLength) {
      getResults({ variables: { resource: resource, term: term, name: name }});
    }
  }, [term, showResults]);

  useEffect(() => {
    if (data) {
      const key = (isInputOption && INPUT_OPTION_KEYS[name]) ||
        `${resource.toLowerCase()}s`;

      setResources(data.autocomplete?.[0]?.[key] || []);
    }
  }, [data]);

  useEffect(() => {
    if (defaultValue) {
      setTerm(resultLabel(defaultValue));
    }
  }, [defaultValue]);

  // InputOption fields submit the text itself; every other resource submits the
  // id of the record that was picked.
  const writeValue = value =>
    setValue(isInputOption ? name : `${resource.toLowerCase()}Id`, value);

  const changeTerm = value => {
    setTerm(value);
    setShowResults(true);

    // Suggestions belong to the previous term, and any record picked under it
    // no longer matches what the field now says.
    setResources([]);
    writeValue(isInputOption ? value : '');
  };

  const selectResult = result => {
    writeValue(isInputOption ? result.label : result.id);

    if (setResource) {
      setResource(result);
    }

    setTerm(resultLabel(result));
    setShowResults(false);

    // Sellers routinely refine a suggestion ("CASCADIA" -> "CASCADIA 126"),
    // so leave them ready to keep typing.
    clearTimeout(blurTimeout.current);
    input.current?.focus();
  };

  const clear = () => {
    setTerm('');
    setResources([]);
    setShowResults(false);
    writeValue('');
  };

  const listVisible = showResults && term.length >= minimumTermLength &&
    resources.length > 0;

  return (
    <View style={{ zIndex: listVisible ? OPEN_LIST_ZINDEX : (zIndex || 9000) }}>
      <Text>{label}:</Text>
      {hint && <Hint>{hint}</Hint>}
      <Container $focused={focused}>
        {term ? (
          <Pressable
            onPress={clear}
            role="button"
            aria-label={`Clear ${label}`}
          >
            <FontAwesomeIcon icon={faXmark} color={colors.disabled} />
          </Pressable>
        ) : (
          <FontAwesomeIcon icon={faMagnifyingGlass} color={colors.disabled} />
        )}
        <Input
          ref={input}
          placeholder={placeHolder}
          placeholderTextColor="#202020"
          value={term}
          onChange={e => changeTerm(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            blurTimeout.current =
              setTimeout(() => setShowResults(false), RESULT_CLICK_GRACE_MS);
          }}
        />
        {listVisible && (
          // Keeping focus on the input means interacting with the list - a
          // click, or a drag of its scrollbar - never races the blur timer.
          <Results onMouseDown={event => event.preventDefault()}>
            {resources.map((result, index) => (
              <Result
                key={`${resultLabel(result)}-${index}`}
                result={result}
                onSelect={selectResult}
              />
            ))}
          </Results>
        )}
      </Container>
    </View>
  );
};

export default Autocomplete;
